'use client'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Accordion } from 'ui/src/components/Accordion/Accordion'
import { PerilColorIcon } from 'ui/src/components/PerilColorIcon/PerilColorIcon'
import { PerilCoverageList } from 'ui/src/components/PerilCoverageList/PerilCoverageList'
import { Text } from 'ui/src/components/Text/Text'
import { tokens } from 'ui/src/theme/tokens.css'
import type { PerilFragment } from '@/services/graphql/generated'
import { useTracking } from '@/services/Tracking/useTracking'
import { useResizeObserver } from '@/utils/useResizeObserver'
import { useProductDataOrNull } from '../ProductData/ProductDataProvider'
import { column, contentWrapper, disabledPeril, grid, header, triggerText } from './Perils.css'

type Peril = PerilFragment & { disabled?: boolean }

const getPerilColumns = (items: Array<Peril>, columns: number) =>
  items.reduce(
    (acc, item, index) => {
      const columnIndex = index % columns
      if (!acc[columnIndex]) {
        acc[columnIndex] = []
      }
      acc[columnIndex].push(item)
      return acc
    },
    [] as Array<Array<Peril>>,
  )

type Props = {
  items: Array<PerilFragment>
  missingItems?: Array<PerilFragment>
}

export const Perils = ({ items, missingItems = [] }: Props) => {
  const perilItems = useMemo<Array<Peril>>(
    () => [...items, ...missingItems.map((item) => ({ ...item, disabled: true }))],
    [items, missingItems],
  )

  const [columns, setColumns] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  // Optimization:
  //
  // We're using lower-level utility compared to potential `useElementSize()` hook because we only care
  // about number of columns as a function of container width
  //
  // Older version used to handle box resize as a state update which effectively meant
  // ~30 renders on every expand/collapse animation
  const handleResize: ResizeObserverCallback = useCallback(([resizeEntry]) => {
    const width = resizeEntry.borderBoxSize[0].inlineSize
    setColumns(getColumnsCount(width))
  }, [])
  useResizeObserver({ elementRef: containerRef, onResize: handleResize })

  const perilsByColumn = getPerilColumns(perilItems, columns)
  return (
    <div className={grid} ref={containerRef}>
      {columns == 0
        ? null // We don't want to render anything before sizing to prevent rendering rework and content jump
        : perilsByColumn.map((perils, index) => (
            <div key={index} className={column}>
              {perils.map((peril) => (
                <PerilsAccordion key={`${index}-${peril.title}`} peril={peril} />
              ))}
            </div>
          ))}
    </div>
  )
}

const getColumnsCount = (width: number) => {
  if (width === 0) return 0
  // Matching peril grid styles below
  const colSize = 18 * 16
  const gapSize = 16
  const extraColumns = Math.max(
    0, // Guard against calculation below going into negatives on small screens
    Math.floor((width - colSize) / (colSize + gapSize)),
  )
  return Math.min(1 + extraColumns, 4)
}

const PerilsAccordion = ({ peril }: { peril: Peril }) => {
  const { title, description, covered, colorCode } = peril
  const [openItems, setOpenItems] = useState<Array<string>>()
  const tracking = useTracking()
  // NOTE: Also used in member area where existing contract is not mapped to a product
  // (and it's not something that's always possible for every contract)
  const productData = useProductDataOrNull()

  const handleValueChange = useCallback(
    (value: Array<string>) => {
      const perilTitle = value.at(0)
      if (perilTitle != null && productData?.name) {
        tracking.reportExpandPeril(productData.name, perilTitle)
      }
      setOpenItems(value)
    },
    [productData?.name, tracking],
  )

  if (peril.disabled) {
    return (
      <div className={disabledPeril}>
        <div className={header}>
          <PerilColorIcon color={tokens.legacyColors.textDisabled} />
          <Text size="lg" color="textSecondary" className={triggerText}>
            {title}
          </Text>
        </div>
      </div>
    )
  }

  return (
    <Accordion.Root type="multiple" value={openItems} onValueChange={handleValueChange}>
      <Accordion.Item key={title} value={title}>
        <Accordion.HeaderWithTrigger className={header}>
          <div className={header}>
            <PerilColorIcon color={colorCode ?? undefined} />
            <Text size="lg" className={triggerText}>
              {title}
            </Text>
          </div>
        </Accordion.HeaderWithTrigger>
        <Accordion.Content open={(openItems ?? []).includes(title)}>
          {/* styles need to be applied to child of content, otherwise height measurement fails */}
          <div className={contentWrapper}>
            <Text as="p" size="xs">
              {description}
            </Text>
            <PerilCoverageList items={covered} />
          </div>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  )
}
